import requestService from "@/service/requestService";
import store from "@/store";
import { clearReservationSearchDataSession } from "@/store/modules/reservationSearchData";
import { SessionService } from "@/service/sessionService";
import { Utils } from "@/service/util";
import { AuthContext, ShakenMode, ShakenStorage } from "@/service/storage";
export class Auth {
  private static suspended = false;

  /** 別タブの認証変更後、再読み込みまでこのタブの認証使用を停止する。 */
  static suspendCurrentTab(): void {
    Auth.suspended = true;
    ShakenStorage.setActiveAuth(null);
    store.dispatch("Authentication/clearTimerRefeshToken");
    store.dispatch("Authentication/clearState");
  }
  timerRefreshToken: any;
  private static lifecycleHookRegistered = false;
  private static readonly REFRESH_INTERVAL_MS = 300000; // 5 minute
  private static readonly SESSION_REFRESH_TIMEOUT_MS = 30000; // 30 seconds
  private static lastSessionOperationAt = 0;
  private static refreshPromise: Promise<boolean> | null = null;
  private static sessionCheckShowsOverlay = false;
  utils = new Utils();
  constructor() {
    if (Auth.suspended) { return; }
    this.migrateLegacyAuthContexts();
    const mode = ShakenStorage.getMode();
    // 各タブは起動時に取り込んだ認証コンテキストを使う。ここで共有
    // localStorageを読み直すと、別タブの後ログインIDへVuexが先に切り替わり、
    // storageイベントの別ID判定ができなくなる。
    const context = ShakenStorage.getActiveAuth(mode);
    const token = context && context.token;
    if (token) {
      store.dispatch("Authentication/setToken", token);
    }
    if (context && context.loginUserId) {
      store.dispatch("Authentication/setUser", context.loginUserId);
    }
  }
  pretendRequest(user: string, pass: string, part: string, cb: any) {
    setTimeout(() => {
      if (user && pass) {
        let req = new requestService();
        req.authentication({ id: user, pass: pass ,assignTypeKbn: part }).then(
          (res: any) => {
            const headerUuid = res.headers["x-shaken-uuid"];
            if (headerUuid) {
              this.utils.setShakenUuid(headerUuid[0], part == "user");
            }
            cb({
              authenticated: true,
              kyotenCd: res.kyotenCd,
              loginTypeKbn: res.loginTypeKbn,
              token: res.token,
              isDebug: res.isDebug
            });
          },
          (error: any) => {
            cb({ authenticated: false, error });
          }
        );
      } else {
        cb({ authenticated: false });
      }
    }, 0);
  }
  login(user: string, pass: string, part: string, cb: any) {
    cb = arguments[arguments.length - 1];
    const mode = ShakenStorage.getMode(part == "user");
    this.pretendRequest(user, pass, part, (res: any) => {
      if (res.authenticated) {
        const context: AuthContext = {
          version: 2,
          // APIがログイン主体IDを返す場合はそちらを優先する。
          loginUserId: res.loginUserId || res.userId || user,
          token: res.token,
          kyotenCd: res.kyotenCd,
          loginTypeKbn: res.loginTypeKbn,
          isDebug: res.isDebug,
          organizationReady: false,
          lastSessionRenewedAt: Date.now(),
          updatedAt: Date.now()
        };
        ShakenStorage.saveAuth(context, mode);
        /** 2021-07-28 sh #6989 when login, set token. [START] */
        /** APIの呼出しで、storeのトークンを使うため、ここで保存しておく */
        store.dispatch("Authentication/setToken", res.token);
        /** 2021-07-28 sh #6989 when login, set token. [END] */
      }
      if (cb) { cb(res); }
    });
  }

  checkAuthen(showOverlay: boolean = false) {
    return this.checkSession(showOverlay);
  }

  static waitForSessionRefresh(): Promise<boolean> {
    return Auth.refreshPromise || Promise.resolve(true);
  }

  /** 業務APIの直前に、必要な場合だけ認証期限を延長する。 */
  static ensureSessionRenewed(): Promise<boolean> {
    return new Auth().ensureSessionRenewed();
  }

  /** 画面遷移時は、更新が必要ならPUT、不要なら従来どおりGETで有効性を確認する。 */
  ensureSessionForScreen(showOverlay: boolean = false): Promise<boolean> {
    if (this.isSessionRenewalRequired()) {
      return this.updateSession(showOverlay);
    }
    return this.checkSession(showOverlay);
  }

  private ensureSessionRenewed(): Promise<boolean> {
    const pendingOperation = Auth.refreshPromise || Promise.resolve(true);
    return pendingOperation.then((isSessionAvailable: boolean) => {
      if (!isSessionAvailable || Auth.suspended || SessionService.hasConnectionProblem()) {
        return false;
      }
      if (!this.isSessionRenewalRequired()) {
        return true;
      }
      return this.updateSession();
    });
  }

  private isSessionRenewalRequired(): boolean {
    const mode = ShakenStorage.getMode();
    const activeContext = ShakenStorage.getActiveAuth(mode);
    const storedContext = ShakenStorage.getAuth(mode);
    // 同一利用者の別タブが更新済みなら、その成功時刻を利用して重複更新を抑制する。
    const context = storedContext && activeContext &&
      storedContext.loginUserId === activeContext.loginUserId ? storedContext : activeContext;
    const lastSessionRenewedAt = context && context.lastSessionRenewedAt;
    return !lastSessionRenewedAt ||
      Date.now() - lastSessionRenewedAt >= Auth.REFRESH_INTERVAL_MS;
  }

  // 初期表示・復帰時の確認。DB上のセッション状態を更新しない。
  private checkSession(showOverlay: boolean): Promise<boolean> {
    if (Auth.refreshPromise) {
      if (showOverlay && !Auth.sessionCheckShowsOverlay) {
        Auth.sessionCheckShowsOverlay = true;
        store.dispatch("Authentication/setSessionChecking", true);
      }
      return Auth.refreshPromise;
    }

    Auth.sessionCheckShowsOverlay = showOverlay;
    if (showOverlay) {
      store.dispatch("Authentication/setSessionChecking", true);
    }
    Auth.lastSessionOperationAt = Date.now();
    const refreshPromise = new Promise<boolean>((resolve, reject) => {
      let req = new requestService();
      req.checkSession(Auth.SESSION_REFRESH_TIMEOUT_MS).then(
        () => {
          SessionService.clearConnectionProblem();
          resolve(true);
        },
        (error: any) => {
          if (error && error.status === 401) {
            resolve(false);
          } else if (error && error.isSessionRefreshTimeout) {
            // 401とは区別し、全画面の通信問題表示と再試行へ進む。
            SessionService.handleSessionRefreshTimeout();
            resolve(false);
          } else {
            SessionService.setConnectionProblem(this.getConnectionProblemReason(error));
            resolve(false);
          }
        }
      );
    });
    Auth.refreshPromise = refreshPromise;
    refreshPromise.then(
      () => this.finishSessionRefresh(refreshPromise),
      () => this.finishSessionRefresh(refreshPromise)
    );
    return refreshPromise;
  }

  private finishSessionRefresh(refreshPromise: Promise<boolean>) {
    if (Auth.refreshPromise === refreshPromise) {
      Auth.refreshPromise = null;
      if (Auth.sessionCheckShowsOverlay) {
        store.dispatch("Authentication/setSessionChecking", false);
        Auth.sessionCheckShowsOverlay = false;
      }
    }
  }

  // 規定間隔でセッションチェックを実行して期限を延ばす
  refreshToken() {
    this.registerLifecycleRefreshTokenEvents();
    this.timerRefreshToken = setInterval(() => {
      this.updateSession().then(
        // 突発的に失敗しても次で成功すればセッションが残るので実行する意味はある。
        () => {},
        (error: any) => {
          // 通信問題はstatus=0だが、ログアウト時はstatus=401で帰ってくる
          if( error.status == 401 ) {
            clearInterval(this.timerRefreshToken);
          }
        }
      );
    }, Auth.REFRESH_INTERVAL_MS);
  }

  // 画面が有効な間の定期更新。セッション状態を更新するのはこの処理だけ。
  private updateSession(showOverlay: boolean = false): Promise<boolean> {
    if (Auth.refreshPromise) {
      if (showOverlay && !Auth.sessionCheckShowsOverlay) {
        Auth.sessionCheckShowsOverlay = true;
        store.dispatch("Authentication/setSessionChecking", true);
      }
      return Auth.refreshPromise;
    }

    Auth.sessionCheckShowsOverlay = showOverlay;
    if (showOverlay) {
      store.dispatch("Authentication/setSessionChecking", true);
    }
    Auth.lastSessionOperationAt = Date.now();
    const refreshPromise = new Promise<boolean>((resolve, reject) => {
      let req = new requestService();
      req.refreshToken(Auth.SESSION_REFRESH_TIMEOUT_MS).then(
        (res: any) => {
          this.saveRefreshedToken(res.body.token);
          SessionService.clearConnectionProblem();
          resolve(true);
        },
        (error: any) => {
          if (error && error.status === 401) {
            resolve(false);
          } else if (error && error.isSessionRefreshTimeout) {
            // 401とは区別し、全画面の通信問題表示と再試行へ進む。
            SessionService.handleSessionRefreshTimeout();
            resolve(false);
          } else {
            SessionService.setConnectionProblem(this.getConnectionProblemReason(error));
            resolve(false);
          }
        }
      );
    });
    Auth.refreshPromise = refreshPromise;
    refreshPromise.then(
      () => this.finishSessionRefresh(refreshPromise),
      () => this.finishSessionRefresh(refreshPromise)
    );
    return refreshPromise;
  }

  private getConnectionProblemReason(error: any): string {
    if (error && error.status >= 500 && error.status <= 599) {
      return "server";
    }
    return "network";
  }

  retrySessionCheck(): Promise<boolean> {
    return this.checkSession(true).then((isAuthenticated: boolean) => {
      if (isAuthenticated && store.getters["Authentication/timerRefeshToken"] == null) {
        this.refreshToken();
        store.dispatch("Authentication/setTimerRefeshToken", this.timerRefreshToken);
      }
      return isAuthenticated;
    });
  }

  private registerLifecycleRefreshTokenEvents() {
    // イベントリスナ重複対策
    if (Auth.lifecycleHookRegistered) {
      return;
    }
    Auth.lifecycleHookRegistered = true;

    // 同一の復帰で複数イベントが発火しても、共通の抑制条件により実通信は1回だけにする。
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        this.requestSessionCheckOnResume();
      }
    });
    window.addEventListener("pageshow", () => this.requestSessionCheckOnResume());
    window.addEventListener("focus", () => this.requestSessionCheckOnResume());
  }

  private requestSessionCheckOnResume() {
    if (document.visibilityState !== "visible" || SessionService.hasConnectionProblem()) {
      return;
    }
    // 前回の認証確認・更新開始から5分未満なら、複数イベント・短時間のフォーカス移動を無視する。
    const elapsed = Date.now() - Auth.lastSessionOperationAt;
    if (elapsed < Auth.REFRESH_INTERVAL_MS) {
      return;
    }
    this.checkAuthen(true).catch(() => {});
  }

  getToken() {
    if (Auth.suspended) { return null; }
    const context = ShakenStorage.getActiveAuth();
    return context ? context.token : window.localStorage.getItem(this.getTokenKey());
  }

  logout(cb: any) {
    let req = new requestService();
    req.logout();
    this.authOut();
    if (cb) { cb(); }
  }

  authOut() {
    const mode = ShakenStorage.getMode();
    ShakenStorage.removeAuth(mode);
    ShakenStorage.removeReservationDetail(mode);
    // 旧版キーを残した環境からの移行期間だけ削除する。
    window.localStorage.removeItem(this.getTokenKey());
    window.localStorage.removeItem("token");
    clearReservationSearchDataSession();
  }

  loggedIn() {
    return !!this.getToken();
  }

  getTokenKey() {
    let tokenKey = "factory";
    if (window.location.href.indexOf("user") > -1) {
      tokenKey = "user";
    /** 2021-07-29 sh #6989 change condition from 'if' to 'else if'. [START] */
    // }
    // if (window.location.href.indexOf("quick") > -1) {
    } else if (window.location.href.indexOf("quick") > -1) {
    /** 2021-07-29 sh #6989 change condition from 'if' to 'else if'. [END] */
      tokenKey = "factory";
    }
    return tokenKey + "token";
  }

  onChange() {}

  private saveRefreshedToken(token: string): void {
    // 警告表示前に送信した更新リクエストの応答で認証を復活させない。
    if (Auth.suspended) { return; }
    store.dispatch("Authentication/setToken", token);
    const context = ShakenStorage.getActiveAuth();
    if (context) {
      const renewedAt = Date.now();
      ShakenStorage.saveAuth({
        ...context,
        token,
        lastSessionRenewedAt: renewedAt,
        updatedAt: renewedAt
      });
    }
  }

  /** 旧キーを全経路分まとめて移行してから削除する。 */
  private migrateLegacyAuthContexts(): void {
    const legacyContexts = [
      { mode: "factory" as ShakenMode, tokenKey: "factorytoken" },
      { mode: "user" as ShakenMode, tokenKey: "usertoken" }
    ];
    let hasLegacyToken = false;
    legacyContexts.forEach(item => {
      const token = window.localStorage.getItem(item.tokenKey);
      if (!token) {
        return;
      }
      hasLegacyToken = true;
      if (!ShakenStorage.getAuth(item.mode)) {
        ShakenStorage.saveAuth({
          version: 2,
          // 旧形式では主体IDを保持していなかったため、次回ログイン時に設定される。
          loginUserId: "",
          token,
          kyotenCd: window.localStorage.getItem("kyotenCd") || undefined,
          loginTypeKbn: window.localStorage.getItem("loginTypeKbn") || undefined,
          isDebug: window.localStorage.getItem("isDebug") === "true" || window.localStorage.getItem("isDebug") === "1",
          groupCd: window.localStorage.getItem("groupCd") || undefined,
          shatenCd: window.localStorage.getItem("shatenCd") || undefined,
          organizationReady: !!window.localStorage.getItem("groupCd") &&
            !!window.localStorage.getItem("shatenCd") &&
            !!window.localStorage.getItem("kyotenCd"),
          updatedAt: Date.now()
        }, item.mode);
      }
    });
    if (hasLegacyToken) {
      [
        "factorytoken", "usertoken", "token", "kyotenCd", "loginTypeKbn",
        "isDebug", "groupCd", "shatenCd", "x-shaken-uuid",
        "x-shaken-uuid-factory", "x-shaken-uuid-user",
        "reservation-detail", "reservation-detail-user", "statusResultTab"
      ].forEach(key => window.localStorage.removeItem(key));
    }
  }
}

